import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Prop, State, VNode } from "@stencil/core";

/**
 * Virtual scroll component that renders a subset of items based on the scroll position.
 * @slot - The slot element that contains the items to be rendered.
 * @internal currently only used in neo components (experimental)
 * @experimental
 */
@Component({
    tag: "ux-virtual-scroll",
    styleUrl: "virtual-scroll.scss",
    shadow: true
})
export class VirtualScroll implements ComponentInterface {
    @Element() el!: HTMLUxVirtualScrollElement;

    /**
     *  Emitted when the scroll end is near.
     */
    @Event() uxScrollEndNear: EventEmitter<void>;
    /**
     * Emitted when the scroll end is reached.
     */
    @Event() uxScrollEnd: EventEmitter<void>;

    /**
     * The number of items to display in the virtual scroll container.
     */
    @Prop() readonly itemCount: number;
    @State() visibleItems: VNode[] = [];
    @State() startIndex = 0;
    @State() buffer = 5; // Number of extra items to keep in the DOM
    @State() debounceTimeout: number | undefined;
    @State() itemHeight = 0; // Add state to store item height
    @State() totalItems = 0; // Add state to store total items count

    componentWillRender() {
        this.#setTotalItems();
        this.#setItemHeight();
        this.#updateVisibleItems();
    }
    componentDidRender(): void {
        this.#setItemHeight();
    }

    componentDidLoad() {
        const slot = this.el.shadowRoot?.querySelector("slot");
        const scrollContainer = this.el.shadowRoot?.querySelector(".virtual-scroll-container");
        if (scrollContainer) {
            scrollContainer.addEventListener("scroll", this.#onScroll.bind(this));
        }
        if (slot) {
            slot.style.display = "none"; // Hide the original slot content
        }
        this.#setItemHeight();
        // Ensure visible items are updated after setting item height
        this.#updateVisibleItems();
    }

    #setItemHeight() {
        const scrollContainer = this.el.shadowRoot?.querySelector(".virtual-scroll-container");
        const firstItem = scrollContainer?.querySelector(".virtual-scroll-item") as HTMLElement;
        if (firstItem) {
            this.itemHeight = firstItem.clientHeight; // Set item height based on the first item
        }
    }

    #setTotalItems() {
        const slot = this.el.shadowRoot?.querySelector("slot");
        const nodes = slot ? slot.assignedNodes() : [];
        const filteredNodes = nodes.filter((node) => node.nodeType === Node.ELEMENT_NODE);
        this.totalItems = filteredNodes.length; // Update total items count
    }

    #onScroll(event: Event) {
        const target = event.target as HTMLElement;
        const scrollTop = target.scrollTop;
        const newStartIndex = Math.floor(scrollTop / this.itemHeight); // Use itemHeight state

        // Check if 80% of the scroll area is reached
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;
        if (scrollTop + clientHeight >= scrollHeight * 0.8) {
            this.uxScrollEndNear.emit(); // Emit the uxScrollEndNear event
        }

        // Check if the scroll end is reached
        if (scrollTop + clientHeight >= scrollHeight) {
            this.uxScrollEnd.emit(); // Emit the uxScrollEnd event
        }

        // Only update if the start index has changed
        if (newStartIndex !== this.startIndex) {
            this.startIndex = newStartIndex;
            if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout);
            }
            this.debounceTimeout = window.setTimeout(() => {
                window.requestAnimationFrame(() => {
                    this.#updateVisibleItems();
                });
            }, 50); // Adjust the debounce delay as needed
        }
    }

    #updateVisibleItems() {
        const slot = this.el.shadowRoot?.querySelector("slot");
        const nodes = slot ? slot.assignedNodes() : [];
        const filteredNodes = nodes.filter((node) => node.nodeType === Node.ELEMENT_NODE);
        this.totalItems = filteredNodes.length; // Update total items count
        const endIndex = Math.min(this.startIndex + this.itemCount + this.buffer, filteredNodes.length);
        this.visibleItems = filteredNodes.slice(this.startIndex, endIndex).map((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
                return <div class="virtual-scroll-item" innerHTML={(node as HTMLElement).outerHTML}></div>;
            }
        });
    }

    render() {
        const totalHeight = this.itemHeight * this.totalItems; // Use totalItems for height calculation
        const topPadding = this.startIndex * this.itemHeight;

        return (
            <Host>
                <div class="virtual-scroll-container" onScroll={(e) => this.#onScroll(e)}>
                    <div
                        id="outer-transform-container"
                        style={{ "--scroll-total-height": `${totalHeight}px`, position: "relative" }}
                    >
                        <div id="inner-transform-container" style={{ "--scroll-padding-y": `${topPadding}px` }}>
                            {this.visibleItems.map((item, index) => (
                                <div class="virtual-scroll-item" key={index} part="scroll">
                                    {item}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <slot
                    onSlotchange={() => {
                        this.#setItemHeight();
                        this.#updateVisibleItems();
                    }}
                ></slot>
            </Host>
        );
    }
}
